/**
 * A grid header that can be dragged to reorder and dragged at its edge to resize.
 *
 * The two gestures share a header, so they must not fight. The resize handle
 * claims the last 6px and stops propagation; anywhere else starts a reorder. That
 * boundary is why the handle is a real element rather than a cursor region — a
 * hit test on pointer position would misfire at the exact pixel where the two
 * meet, which is precisely where users aim.
 *
 * A plain click sorts by the column (when it can sort): once ascending, again
 * descending. A drag never counts as a click, so reordering does not sort.
 *
 * Resizing tracks the pointer with a capture rather than listening on the window,
 * so releasing outside the window still ends the drag instead of leaving the grid
 * stuck in resize mode.
 */

import { useRef } from "react";
import type { ColumnDef } from "./columns";

export function HeaderCell({
  column,
  onResize,
  onReorder,
  dragging,
  onDragState,
  onMenu,
  sorted,
  onSort,
}: {
  column: ColumnDef;
  onResize: (id: string, width: number) => void;
  onReorder: (fromId: string, toId: string) => void;
  dragging: string | null;
  onDragState: (id: string | null) => void;
  onMenu?: (columnId: string, at: { x: number; y: number }) => void;
  /** This column's sort direction when the grid is sorted by it. */
  sorted?: "asc" | "desc" | null;
  /** Sort by this column; absent for columns that cannot sort. */
  onSort?: (columnId: string) => void;
}) {
  const ref = useRef<HTMLTableCellElement>(null);
  const resizing = useRef(false);

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;

    const startX = e.clientX;
    const startWidth = column.width;
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const move = (ev: PointerEvent) =>
      onResize(column.id, startWidth + (ev.clientX - startX));
    const end = (ev: PointerEvent) => {
      resizing.current = false;
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", end);
      target.removeEventListener("pointercancel", end);
    };

    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", end);
    target.addEventListener("pointercancel", end);
  };

  const isDropTarget = dragging !== null && dragging !== column.id;

  return (
    <th
      ref={ref}
      style={{ width: column.width, minWidth: column.minWidth }}
      className={[
        column.align === "right" ? "th-right" : "",
        onSort ? "th-sortable" : "",
        sorted ? "th-sorted" : "",
        dragging === column.id ? "th-dragging" : "",
        isDropTarget ? "th-droppable" : "",
      ].join(" ")}
      draggable={!column.locked}
      onDragStart={(e) => {
        if (resizing.current) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", column.id);
        onDragState(column.id);
      }}
      onDragEnd={() => onDragState(null)}
      onClick={() => {
        if (!resizing.current) onSort?.(column.id);
      }}
      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : undefined}
      onContextMenu={(e) => {
        if (!onMenu) return;
        e.preventDefault();
        onMenu(column.id, { x: e.clientX, y: e.clientY });
      }}
      onDragOver={(e) => {
        if (isDropTarget) e.preventDefault();
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = e.dataTransfer.getData("text/plain");
        if (from && from !== column.id) onReorder(from, column.id);
        onDragState(null);
      }}
      title={[column.label, onSort ? "click to sort" : "", column.locked ? "" : "drag to reorder, right-click for more"].filter(Boolean).join(" — ")}
    >
      <span className="th-label">{column.label}</span>
      {sorted && <span className="th-arrow" aria-hidden>{sorted === "asc" ? "▲" : "▼"}</span>}
      <span
        className="th-resize"
        onPointerDown={startResize}
        onDragStart={(e) => e.preventDefault()}
        title="Drag to resize"
      />
    </th>
  );
}
